import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MessageSquareReply, Star, Trash2 } from "lucide-react";

export const ReviewsAdmin = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drafts, setDrafts] = useState({});

    const load = useCallback(() => {
        api.get("/reviews").then((r) => {
            setReviews(r.data);
            setDrafts(Object.fromEntries(r.data.map((x) => [x.id, x.reply?.text || ""])));
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);
    useEffect(() => {
        load();
    }, [load]);

    const remove = async (r) => {
        try {
            await api.delete(`/admin/reviews/${r.id}`);
            toast.success(`Bewertung von „${r.name}" entfernt.`);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const saveReply = async (r) => {
        try {
            await api.patch(`/admin/reviews/${r.id}`, { reply: drafts[r.id] || "" });
            toast.success(drafts[r.id] ? "Antwort veröffentlicht — sichtbar unter der Bewertung." : "Antwort entfernt.");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div data-testid="admin-reviews">
            {loading && <p className="text-sm text-[#9C917D]">Lade Bewertungen …</p>}
            {!loading && reviews.length === 0 && <p className="text-sm text-[#9C917D]">Noch keine Bewertungen vorhanden.</p>}
            <div className="space-y-4">
                {reviews.map((r) => (
                    <div key={r.id} className="rounded border border-[#2E2517] bg-[#1B150E] p-6" data-testid={`admin-review-${r.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <p className="font-semibold text-[#EDE6DA]">{r.name}</p>
                                <span className="flex gap-0.5">
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <Star key={i} size={13} className={i <= r.rating ? "fill-[#C88A45] text-[#C88A45]" : "text-[#2E2517]"} />
                                    ))}
                                </span>
                            </div>
                            <button
                                data-testid={`admin-review-delete-${r.id}`}
                                onClick={() => remove(r)}
                                className="flex items-center gap-2 rounded-full border border-[#2E2517] px-4 py-1.5 text-xs text-[#9C917D] transition-colors duration-200 hover:border-red-400/40 hover:text-red-400"
                            >
                                <Trash2 size={13} /> Entfernen
                            </button>
                        </div>
                        <p className="mt-3 text-sm leading-relaxed text-[#D9D2C4]">{r.text}</p>
                        {r.photo_path && (
                            <img src={`${API}/files/${r.photo_path}`} alt="" className="img-warm mt-4 max-h-48 rounded border border-[#2E2517] object-cover" />
                        )}
                        <div className="mt-4 flex items-center gap-2">
                            <Input
                                data-testid={`admin-review-reply-${r.id}`}
                                value={drafts[r.id] || ""}
                                onChange={(e) => setDrafts({ ...drafts, [r.id]: e.target.value })}
                                placeholder='Ihre Antwort, z. B. „Vielen Dank, gern wieder!" — erscheint öffentlich'
                                className="h-9 border-[#2E2517] bg-[#120E09] text-sm text-[#EDE6DA]"
                            />
                            <button
                                data-testid={`admin-review-reply-save-${r.id}`}
                                onClick={() => saveReply(r)}
                                className="flex shrink-0 items-center gap-2 rounded-full bg-[#C88A45] px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#120E09] transition-colors duration-200 hover:bg-[#DBA057]"
                            >
                                <MessageSquareReply size={13} /> Antworten
                            </button>
                        </div>
                        <p className="mt-3 text-xs text-[#9C917D]">{new Date(r.created_at).toLocaleString("de-DE")}</p>
                    </div>
                ))}
            </div>
        </div>
    );
};
